/**
 * The sustain pedal.
 *
 * Pedal is its own parallel stream, graded with the same onset / offset tools as notes on a
 * separate track, and only when a preset judges it. It also changes what can be judged under
 * it: while the sustain pedal is down the player hears the pedal, not the key release, so a
 * note whose key is released inside a pedal-down span has its offset and duration not judged
 * (pitch, onset and velocity still are). Sustain only: sostenuto and soft suspend nothing.
 */

import { Clock } from './clock';
import { Combiner } from './noteJudgment';
import { CreditCurve, creditAt, creditCurveErrors } from './creditCurve';
import { BINDING_WINDOW_BEATS } from './matching';
import { PedalEvent, ScorePedalMark } from './types';

/** MIDI CC64 at or above this value means the sustain pedal is down. */
export const SUSTAIN_DOWN_THRESHOLD = 64;

export interface PedalSpan {
  readonly downMs: number;
  /** Null while the pedal is still down. */
  readonly upMs: number | null;
}

export interface SustainState {
  readonly down: boolean;
  readonly spans: readonly PedalSpan[];
}

export const NO_SUSTAIN: SustainState = { down: false, spans: [] };

/** Folds one pedal event into the sustain state. Non-sustain pedals change nothing. */
export function applyPedalEvent(state: SustainState, event: PedalEvent): SustainState {
  if (event.pedal !== 'sustain') {
    return state;
  }
  const pressedDown = event.value >= SUSTAIN_DOWN_THRESHOLD;
  if (pressedDown === state.down) {
    return state;
  }
  if (pressedDown) {
    return { down: true, spans: [...state.spans, { downMs: event.timeMs, upMs: null }] };
  }
  const closed = state.spans.map((span, index) =>
    index === state.spans.length - 1 ? { ...span, upMs: event.timeMs } : span,
  );
  return { down: false, spans: closed };
}

export interface PedalStreamConfig {
  /** Curve for when the pedal goes down, against the notated pedal-down point. */
  readonly onset?: CreditCurve;
  /** Curve for when the pedal comes up, against the notated pedal-up point. */
  readonly offset?: CreditCurve;
  readonly combiner: Combiner;
}

export function pedalStreamErrors(config: PedalStreamConfig): string[] {
  const curves: [string, CreditCurve | undefined][] = [
    ['pedal onset', config.onset],
    ['pedal offset', config.offset],
  ];
  return curves.reduce<string[]>((errors, [label, curve]) => {
    if (curve === undefined) {
      return errors;
    }
    const unitErrors =
      curve.unit === 'ms' || curve.unit === 'beats' ? [] : [`${label}: unit must be ms or beats`];
    const sideErrors =
      curve.sides === undefined ? [] : [`${label}: per-side settings are for note dimensions only`];
    return [
      ...errors,
      ...unitErrors,
      ...sideErrors,
      ...creditCurveErrors(curve).map((e) => `${label}: ${e}`),
    ];
  }, []);
}

export type PedalMarkStatus = 'matched' | 'missed' | 'pending' | 'unreached';

export interface PedalJudgment {
  readonly markId: string;
  readonly downBeat: number;
  /** When the pedal went down (or, if missed, when it was due); null until reached. */
  readonly timeMs: number | null;
  readonly status: PedalMarkStatus;
  readonly downDeviationMs: number | null;
  readonly upDeviationMs: number | null;
  /** Null until the mark is reached. */
  readonly credit: number | null;
  readonly settled: boolean;
}

function toUnit(curve: CreditCurve, deviationMs: number, msPerBeat: number): number {
  return curve.unit === 'beats' ? deviationMs / msPerBeat : deviationMs;
}

function combinePedal(combiner: Combiner, credits: readonly number[]): number {
  if (credits.length === 0) {
    return 1;
  }
  if (combiner === 'worst-of') {
    return Math.min(...credits);
  }
  if (combiner === 'product') {
    return credits.reduce((product, credit) => product * credit, 1);
  }
  return credits.reduce((sum, credit) => sum + credit, 0) / credits.length;
}

/**
 * Judges the pedal stream: each notated pedal mark, in order, binds to the first unused
 * performed span whose pedal-down falls inside the mark's binding window.
 */
export function judgePedalStream(
  marks: readonly ScorePedalMark[],
  sustain: SustainState,
  clock: Clock,
  config: PedalStreamConfig,
  nowMs: number,
  finished: boolean,
): PedalJudgment[] {
  const ordered = [...marks].sort((a, b) => a.downBeat - b.downBeat);
  return ordered.reduce<{ judgments: PedalJudgment[]; used: readonly number[] }>(
    (state, mark) => {
      const dueMs = clock.msAtBeat(mark.downBeat);
      const msPerBeat = clock.msPerBeatAt(mark.downBeat);
      const startMs = dueMs - BINDING_WINDOW_BEATS.early * msPerBeat;
      const endMs = dueMs + BINDING_WINDOW_BEATS.late * msPerBeat;
      const spanIndex = sustain.spans.findIndex(
        (span, index) =>
          state.used.indexOf(index) < 0 && span.downMs >= startMs && span.downMs <= endMs,
      );
      if (spanIndex < 0) {
        const windowClosed = finished || nowMs > endMs;
        const status: PedalMarkStatus = windowClosed
          ? finished && dueMs > nowMs
            ? 'unreached'
            : 'missed'
          : dueMs > nowMs
            ? 'unreached'
            : 'pending';
        const judgment: PedalJudgment = {
          markId: mark.id,
          downBeat: mark.downBeat,
          timeMs: status === 'missed' ? dueMs : null,
          status,
          downDeviationMs: null,
          upDeviationMs: null,
          credit: status === 'missed' ? 0 : null,
          settled: status === 'missed' || status === 'unreached',
        };
        return { judgments: [...state.judgments, judgment], used: state.used };
      }
      const span = sustain.spans[spanIndex];
      const upDueMs = clock.msAtBeat(mark.upBeat);
      const downDeviationMs = span.downMs - dueMs;
      const upAtMs = span.upMs ?? (finished ? nowMs : null);
      const upDeviationMs = upAtMs === null ? null : upAtMs - upDueMs;
      const credits = [
        config.onset
          ? creditAt(config.onset, toUnit(config.onset, downDeviationMs, msPerBeat))
          : null,
        config.offset && upDeviationMs !== null
          ? creditAt(config.offset, toUnit(config.offset, upDeviationMs, msPerBeat))
          : null,
      ].filter((credit): credit is number => credit !== null);
      const judgment: PedalJudgment = {
        markId: mark.id,
        downBeat: mark.downBeat,
        timeMs: span.downMs,
        status: 'matched',
        downDeviationMs,
        upDeviationMs,
        credit: combinePedal(config.combiner, credits),
        settled: config.offset === undefined || upDeviationMs !== null,
      };
      return { judgments: [...state.judgments, judgment], used: [...state.used, spanIndex] };
    },
    { judgments: [], used: [] },
  ).judgments;
}
